import {
  FileUploadStatusT,
  FormattingOption,
  InputRichText,
  InputRichTextProps,
  Text,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import EmojiOne from 'emojione';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import noop from 'lodash/noop';
import * as React from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { arrayPush } from 'redux-form';

import { filesApi } from 'apis/files';
import { getFileSize } from 'app/utils/attachments';
import {
  channelToSanitizationProfile,
  SanitizeHTML,
} from 'app/utils/htmlSanitizer';
import markdownToHTML from 'app/utils/markdownToHTML';
import supportedLanguages, {
  DEFAULT_LANG_CODE,
} from 'app/utils/supportedLanguages';
import { getCurrentConversationLanguage } from 'components/conversation/selectors';
import * as personalActions from 'components/settings/actions/personal';
import {
  CommandGroup,
  commandGroupDelimiter,
  useCommands,
} from 'utils/commands';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { StateBar } from './components/stateBar/stateBar';
import type { ValidationState } from './components/stateBar/stateBar.types';
import styles from './richTextField.module.scss';
import type { Channel } from './richTextField.types';
import { usePendingAttachments } from './utils/usePendingAttachments';
import { useWhatsAppExclusiveMode } from './utils/useWhatsAppExclusiveMode';
import type { AttachmentCandidate } from './utils/validateAttachmentBatch';
import { validateAttachmentBatch } from './utils/validateAttachmentBatch';

const { personalSubmitMessageWithEnter } = personalActions.actions;

const MAX_INLINE_FILE_SIZE = 100000000; // ~100mb.
let formIdCounter = 0;

export type RichTextFieldProps = {
  value?: string;
  channel: Channel;
  sanitize?: (html: string) => string;
  language?: string;
  disableSubmitMessageWithEnter?: boolean;
  requesterName?: string;
  className?: string;
  stateBarClassName?: string;
  label?: string;
  name?: string;
  required?: boolean;
  placeholder?: string;
  attachments?: File[];
  attachmentsFields?: any;
  onAttachmentAdded?: (attachment: {
    name: string;
    absolute_uri: string;
    relative_uri: string;
    size: number;
  }) => void;
  onFileIsUploadingChange?: (state: boolean) => void;
  /** Redux form name */
  form?: string;
  onChange?: (value: string) => void;
  onTriggerSend?: () => void;
  /**
   * Surfaces the state of the message in the state bar. A selection of files
   * that was turned away does not travel this way: it is shown as a notice
   * beside the attachment picker instead.
   */
  setValidationState?: (validation: ValidationState) => void;
  validation?: any;
  hasBorder?: boolean;
  /** Applied to the outer wrapper div. */
  editorClassName?: string;
  /** Applied to InputRichText's wrapperClassName */
  wrapperClassName?: string;
  id?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  autocomplete?: {
    hasAutocompleteSuggestionsFeature: boolean;
    showAutocompleteSuggestions: boolean;
    onToggleShowAutocompleteSuggestions: () => void;
  };
  hideSendOnEnterToggle?: boolean;
  isPublicReply?: boolean;
  toolbarAddons?: React.ReactNode;
  toolbarEnd?: React.ReactNode;
  disableAttachments?: boolean;
  disableInlineAttachments?: boolean;
  disableEmoji?: boolean;
};

export type RichTextFieldRef = {
  getText: () => string;
  getPlainText: () => string;
  setText: (text: string) => void;
  setMarkdown: (text: string) => void;
  focus: () => void;
  moveCursorToEnd: () => void;
};

const RichTextField = forwardRef<RichTextFieldRef, RichTextFieldProps>(
  function RichTextField(
    {
      value,
      channel,
      sanitize,
      disableSubmitMessageWithEnter = false,
      hideSendOnEnterToggle,
      requesterName,
      placeholder,
      id,
      className,
      label,
      required,
      attachmentsFields,
      attachments,
      setValidationState,
      validation,
      hasBorder,
      editorClassName,
      wrapperClassName,
      autocomplete,
      stateBarClassName,
      isPublicReply,
      toolbarAddons,
      toolbarEnd,
      form,
      onChange,
      onKeyDown = noop,
      onTriggerSend,
      onFileIsUploadingChange,
      onAttachmentAdded,
      disableAttachments,
      disableInlineAttachments,
      disableEmoji,
    },
    ref,
  ): JSX.Element {
    const editorRef = useRef<InputRichText | null>(null);

    const dispatch = useAppDispatch();
    const flags = useFeatureFlag(['templates-language-translation-control']);
    const { keybindings } = useCommands();

    const submitMessageWithEnter = useAppSelector(
      (state) => state.settings.personal.submitMessageWithEnter,
    );
    const language =
      useAppSelector(getCurrentConversationLanguage) ?? DEFAULT_LANG_CODE;

    const [uploads, setUploads] = useState<Record<string, FileUploadStatusT>>(
      {},
    );

    // Attachments still uploading. `uploads` cannot serve this purpose: it is
    // shared with inline image uploads and its entries are never cleared.
    const { pendingFiles, addPendingAttachments, removePendingAttachment } =
      usePendingAttachments();

    // Why the last selection of files was turned away, worded for the agent,
    // or null when nothing was.
    const [attachmentRejection, setAttachmentRejection] = useState<
      string | null
    >(null);

    // A file finishing its upload (moves from pending to attached) does not
    // read as a change.
    const attachmentCount =
      (Array.isArray(attachments) ? attachments.length : 0) +
      pendingFiles.length;

    // Clear the rejection notice on attachment count change, after which
    // the notice is no longer relevant
    useLayoutEffect(() => {
      setAttachmentRejection(null);
    }, [attachmentCount]);

    const [reduxForm] = useState<string>(() => {
      if (form) {
        return form;
      }
      formIdCounter++;
      return `rich-text-field-${formIdCounter}`;
    });

    const [editorPlainText, setEditorPlainText] = useState<string>('');

    const isUploading = useMemo(() => {
      return Object.values(uploads).some((u) => u.status === 'loading');
    }, [uploads]);

    useEffect(() => {
      if (typeof onFileIsUploadingChange === 'function') {
        onFileIsUploadingChange(isUploading);
      }
    }, [isUploading, onFileIsUploadingChange]);

    // WhatsApp exclusive mode (text OR attachment, not both)
    const {
      hasAttachments,
      isAttachmentsDisabledByText,
      isTextDisabledByAttachment,
      attachmentsDisabledTooltip,
      textDisabledPlaceholder,
    } = useWhatsAppExclusiveMode({
      channel,
      editorPlainText,
      attachments,
    });

    useEffect(() => {
      // Clear the notice when the attachment becomes disabled
      if (isAttachmentsDisabledByText) {
        setAttachmentRejection(null);
      }
    }, [isAttachmentsDisabledByText]);

    //#reqion Publicly exposed methods
    const getText = useCallback((): string => {
      const editor = editorRef.current;
      if (!editor) {
        return '';
      }
      return editor.getSanitizedHtml();
    }, []);

    const getPlainText = useCallback((): string => {
      const editor = editorRef.current;
      if (!editor) {
        return '';
      }
      return editor.getPlainText();
    }, []);

    const setText = useCallback((text: string): void => {
      const editor = editorRef.current;
      if (!editor) {
        return;
      }
      editor.setHtml(text);
    }, []);

    const setMarkdown = useCallback(
      (markdown: string): void => {
        const markup = markdownToHTML(EmojiOne.shortnameToUnicode(markdown));
        const fixParagraphs = markup
          .replace(/<p>/g, '<div>')
          .replace(/<\/p>/g, '</div><br />');
        setText(fixParagraphs);
      },
      [setText],
    );

    const focus = useCallback((): void => {
      const editor = editorRef.current;
      if (!editor) {
        return;
      }

      editor.focus();
    }, []);

    const moveCursorToEnd = useCallback((): void => {
      const editor = editorRef.current;
      if (!editor) {
        return;
      }

      editor.moveCursorToEnd();
    }, []);

    useImperativeHandle(ref, () => {
      return {
        getText,
        getPlainText,
        setText,
        setMarkdown,
        focus,
        moveCursorToEnd,
      };
    }, [getText, getPlainText, setText, setMarkdown, focus, moveCursorToEnd]);
    //#endregion

    //#region Event handlers
    const handleToggleSendOnEnter = useCallback((): void => {
      dispatch(
        personalSubmitMessageWithEnter({
          key: 'submitMessageWithEnter',
          value: !submitMessageWithEnter,
        }),
      );
    }, [dispatch, submitMessageWithEnter]);

    const handleTriggerSend = useCallback((): void => {
      // Whatever was turned away belongs to the message being left behind.
      setAttachmentRejection(null);

      if (typeof onTriggerSend === 'function') {
        onTriggerSend();
      }
    }, [onTriggerSend]);

    const handleAttachmentDropdownToggle = useCallback(
      (isOpen: boolean): void => {
        if (!isOpen) {
          setAttachmentRejection(null);
        }
      },
      [],
    );

    const handleChange = useCallback((): void => {
      // wrap in setTimeout, because the event is fired before the inner state of the editor is updated
      setTimeout(() => {
        const text = getText();
        const plainText = getPlainText();

        setEditorPlainText(plainText);

        if (typeof onChange === 'function') {
          onChange(text);
        }
      }, 1);
    }, [onChange, getText, getPlainText]);

    // Read through a ref rather than closed over, because a single drop calls
    // `handleInlineUpload` once per file from the same closure: the file that
    // sets the warning and the file that should clear it would otherwise both
    // see the state from before either ran. By the time an upload resolves the
    // effect below has long since run.
    const warningTypeRef = useRef<string | undefined>(
      validation?.warning?.type,
    );

    useEffect(() => {
      warningTypeRef.current = validation?.warning?.type;
    }, [validation?.warning?.type]);

    const handleInlineUpload = useCallback(
      (file: File, uid: string): void => {
        setUploads((prevUploads) => ({
          ...prevUploads,
          [uid]: { status: 'loading', filename: file.name },
        }));

        if (file.size > MAX_INLINE_FILE_SIZE) {
          // Reported through the validation state rather than the attachment
          // notice: the notice lives inside the attach-button dropdown, which
          // an inline drop never opens.
          setValidationState?.({
            touched: true,
            valid: true,
            warning: {
              type: 'file-size',
              arg: {
                size: getFileSize(file.size),
                maxSize: getFileSize(MAX_INLINE_FILE_SIZE),
              },
            },
          });

          // Outside the `setValidationState` branch on purpose: a consumer that
          // does not pass the callback would otherwise be left with a loader
          // that spins forever.
          setUploads((prevUploads) => ({
            ...prevUploads,
            [uid]: { status: 'failed', filename: file.name },
          }));
        } else {
          dispatch(
            filesApi.endpoints.createAttachment.initiate({
              access: 'public',
              file: file,
            }),
          )
            .unwrap()
            .then((response) => {
              setUploads((prevUploads) => ({
                ...prevUploads,
                [uid]: {
                  status: 'succeeded',
                  link: response.data.absolute_uri,
                  filename: file.name,
                },
              }));

              // A file that landed answers an earlier one that was too large.
              // Guarded, so an unrelated warning is not swept away with it.
              if (warningTypeRef.current === 'file-size') {
                setValidationState?.({
                  touched: true,
                  valid: true,
                  warning: undefined,
                });
              }
            })
            .catch((exception: unknown) => {
              setUploads((prevUploads) => ({
                ...prevUploads,
                [uid]: { status: 'failed', filename: file.name },
              }));
              console.warn('handleInlineUpload exception', exception);
            });
        }
      },
      [dispatch, setValidationState],
    );

    const handleAttachmentUpload = useCallback(
      (candidates: AttachmentCandidate[]): void => {
        // The whole selection is validated as one unit, counting pending files and those
        // already uploaded and added to the form
        const rejection = validateAttachmentBatch({
          candidates,
          attachedFiles: Array.isArray(attachments) ? attachments : [],
          pendingFiles,
          channel,
        });

        // A violation rejects the entire selection, so the user picks again
        // instead of guessing which files made it through.
        if (rejection) {
          setAttachmentRejection(rejection);
          return;
        }

        setAttachmentRejection(null);
        addPendingAttachments(candidates);

        candidates.forEach(({ file, uid }) => {
          setUploads((prevUploads) => ({
            ...prevUploads,
            [uid]: { status: 'loading' },
          }));

          dispatch(
            filesApi.endpoints.createAttachment.initiate({
              file,
              access: [
                'adachat',
                'digitalgeniuschat',
                'kindlychat',
                'genericapimessaging',
                'widgetchat',
                'messenger',
                'whatsapp',
              ].includes(channel)
                ? 'public'
                : 'private',
            }),
          )
            .unwrap()
            .then((response) => {
              const attachment = {
                name: file.name,
                absolute_uri: response.data.absolute_uri,
                relative_uri: response.data.relative_uri,
                size: file.size,
                uid: uid,
              };

              setUploads((prevUploads) => ({
                ...prevUploads,
                [uid]: {
                  status: 'succeeded',
                  link: response.data.absolute_uri,
                },
              }));

              dispatch(arrayPush(reduxForm, 'attachments', attachment));
              removePendingAttachment(uid);
              onAttachmentAdded?.(attachment);
            })
            .catch((exception: unknown) => {
              setUploads((prevUploads) => ({
                ...prevUploads,
                [uid]: { status: 'failed' },
              }));
              removePendingAttachment(uid);

              console.warn('handleAttachmentUpload exception', exception);
            });
        });
      },
      [
        dispatch,
        channel,
        reduxForm,
        setUploads,
        attachments,
        pendingFiles,
        addPendingAttachments,
        removePendingAttachment,
        onAttachmentAdded,
      ],
    );

    const handleInputError = useCallback((error: Error): void => {
      if (error.name === 'IndexSizeError') {
        Sentry.captureException(error, {
          tags: { team: SentryTeam.Core },
        });
        return;
      }
      throw error;
    }, []);

    //#endregion

    //#region calculated props passed to InputRichText

    const inputKeybindings = useMemo(() => {
      return Object.fromEntries(
        Object.entries(keybindings)
          .filter(([commandName]) =>
            commandName.startsWith(richTextEditorGroup),
          )
          .map(([commandName, keyCombinations]) => {
            return [
              commandName.replace(
                `${richTextEditorGroup}${commandGroupDelimiter}`,
                '',
              ),
              keyCombinations.map((keyCombination) => keyCombination.binding),
            ];
          }),
      ) as InputRichTextProps['keybindings'];
    }, [keybindings]);

    const inputHideSendOnEnterToggle = useMemo(() => {
      return (
        ['email', 'contactform', 'notes', 'bulk'].includes(channel) ||
        hideSendOnEnterToggle
      );
    }, [channel, hideSendOnEnterToggle]);

    const inputSendOnEnter = useMemo(() => {
      return (
        !disableSubmitMessageWithEnter &&
        submitMessageWithEnter &&
        !['email', 'contactform', 'notes', 'bulk'].includes(channel)
      );
    }, [disableSubmitMessageWithEnter, submitMessageWithEnter, channel]);

    const inputAllowFormatting: boolean | FormattingOption[] = useMemo(() => {
      if (channel === 'whatsapp') {
        return ['bold', 'italic', 'link', 'orderedList', 'unorderedList'];
      }

      return [
        'email',
        'contactform',
        'widgetchat',
        'notes',
        'messenger',
      ].includes(channel);
    }, [channel]);

    const inputPlaceholderText = useMemo(() => {
      const suffix = inputSendOnEnter ? ' (send with <enter>)' : '';
      return placeholder
        ? placeholder
        : requesterName
          ? `Message ${requesterName}${suffix}`
          : `Type your message${suffix}`;
    }, [placeholder, requesterName, inputSendOnEnter]);

    const inputLanguages = useMemo(() => {
      if (!flags.templatesLanguageTranslationControl) {
        return undefined;
      }

      return supportedLanguages.concat().sort((a, b) => {
        if (a.code === language) {
          return -1;
        }
        if (b.code === language) {
          return 1;
        }
        return 0;
      });
    }, [flags.templatesLanguageTranslationControl, language]);

    const inputAllowInlineAttachments = useMemo(() => {
      return (
        ['email', 'contactform', 'notes'].includes(channel) &&
        !disableInlineAttachments
      );
    }, [channel, disableInlineAttachments]);

    const inputAllowAttachments = useMemo(() => {
      const channelsWithAttachmentSupport: Channel[] = [
        'adachat',
        'digitalgeniuschat',
        'kindlychat',
        'genericapimessaging',
        'email',
        'contactform',
        'widgetchat',
        'messenger',
        'whatsapp',
      ];

      return (
        !disableAttachments && channelsWithAttachmentSupport.includes(channel)
      );
    }, [channel, disableAttachments]);

    const inputSanitize = useMemo(() => {
      if (typeof sanitize === 'function') {
        return sanitize;
      }

      const sanitizationProfile = channelToSanitizationProfile[channel];

      return SanitizeHTML.sanitizerForProfile(sanitizationProfile);
    }, [channel, sanitize]);

    //#endregion

    return (
      <div className={classnames(styles.editor, editorClassName)}>
        {(required || (!required && label)) && (
          <label className="control-label" htmlFor={undefined}>
            {label} {required ? '*' : ''}
          </label>
        )}
        {validation && (
          <StateBar
            channel={channel}
            text={editorPlainText}
            validation={validation}
            className={stateBarClassName}
            isPublicReply={isPublicReply}
            allowEmptyText={channel === 'whatsapp' && hasAttachments}
          />
        )}

        <InputRichText
          id={id}
          name=""
          value={value ?? ''}
          keybindings={inputKeybindings}
          onKeyDown={onKeyDown}
          autocomplete={autocomplete}
          wrapperClassName={classnames(
            styles.editorWrapper,
            {
              [styles.editorWrapperNoBorder]: !hasBorder,
            },
            wrapperClassName,
          )}
          className={classnames(className, styles.editorRoot)}
          ref={editorRef}
          languages={inputLanguages}
          toolbarAddons={toolbarAddons}
          toolbarEnd={toolbarEnd}
          sendOnEnter={inputSendOnEnter}
          hideSendOnEnterToggle={inputHideSendOnEnterToggle}
          placeholder={textDisabledPlaceholder ?? inputPlaceholderText}
          fileUploadStatuses={uploads}
          allowFormatting={inputAllowFormatting}
          allowInlineAttachments={inputAllowInlineAttachments}
          allowAttachments={inputAllowAttachments}
          attachmentsDisabled={isAttachmentsDisabledByText}
          attachmentsDisabledTooltip={attachmentsDisabledTooltip}
          textInputDisabled={isTextDisabledByAttachment}
          allowEmoji={!disableEmoji}
          onAttachmentDropdownToggle={handleAttachmentDropdownToggle}
          attachmentNotice={
            attachmentRejection && (
              <Text.Input variant="warning" padding="200 200 0 200">
                {attachmentRejection}
              </Text.Input>
            )
          }
          attachmentsFields={attachmentsFields}
          isAttachmentsUploading={isUploading}
          attachments={attachments}
          sanitize={inputSanitize}
          onChange={handleChange}
          onTriggerSend={handleTriggerSend}
          onToggleSendOnEnter={handleToggleSendOnEnter}
          onFileDropped={handleInlineUpload}
          onAttachmentsSelected={handleAttachmentUpload}
          onError={handleInputError}
        />
      </div>
    );
  },
);

const richTextEditorGroup: CommandGroup = 'richTextEditor';
export default RichTextField;
